import { MaterialCommunityIcons } from "@expo/vector-icons";
import { useEffect, useRef, useState } from "react";
import { StyleSheet, View } from "react-native";
import { Text, TextInput } from "react-native-paper";

import type { QuestionComponentProps } from "./types";

import { colors, fonts } from "@/lib/theme";

/**
 * Strip every non-digit and cap at 10. Tolerates any inbound format —
 * "+12127185430", "(212) 718-5430", "212.718.5430" all normalize to
 * "2127185430".
 */
function digitsOnly(s: string): string {
    return s.replace(/\D/g, "").slice(-10);
}

/**
 * Progressive display formatting as the RN types.
 *   ""           → ""
 *   "2"          → "(2"
 *   "212"        → "(212) "
 *   "2127"       → "(212) 7"
 *   "212718"     → "(212) 718"
 *   "2127185"    → "(212) 718-5"
 *   "2127185430" → "(212) 718-5430"
 */
function formatDisplay(digits: string): string {
    if (digits.length === 0) {
        return "";
    }
    if (digits.length <= 3) {
        return `(${digits}`;
    }
    if (digits.length <= 6) {
        return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    }
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

export function PhoneQuestion({
    question,
    value,
    onChange,
    readOnly,
    onFocus,
    onBlur
}: QuestionComponentProps) {
    const [digits, setDigits] = useState<string>(() => digitsOnly(value ?? ""));
    const [touched, setTouched] = useState(false);
    const editingRef = useRef(false);

    // Sync from parent when not actively editing (e.g. server hydration).
    useEffect(() => {
        if (!editingRef.current) {
            setDigits(digitsOnly(value ?? ""));
        }
    }, [value]);

    const handleChange = (text: string) => {
        editingRef.current = true;
        const next = digitsOnly(text);
        setDigits(next);
        onChange(next);
    };

    const handleBlur = () => {
        editingRef.current = false;
        setTouched(true);
        onBlur?.();
    };

    const isComplete = digits.length === 10;
    const showError = touched && !isComplete && digits.length > 0;

    return (
        <View>
            <TextInput
                mode="outlined"
                value={formatDisplay(digits)}
                onChangeText={handleChange}
                disabled={readOnly}
                placeholder={question.hintText ?? "(XXX) XXX-XXXX"}
                keyboardType="phone-pad"
                inputMode="tel"
                maxLength={14}
                onFocus={() => {
                    editingRef.current = true;
                    onFocus?.();
                }}
                onBlur={handleBlur}
                style={styles.input}
                dense
                outlineColor={showError ? colors.error500 : undefined}
                activeOutlineColor={showError ? colors.error500 : undefined}
            />
            {showError ? (
                <View style={styles.chip}>
                    <MaterialCommunityIcons
                        name="alert-outline"
                        size={14}
                        color={colors.error500}
                    />
                    <Text style={styles.chipText}>
                        Phone must be 10 digits ({digits.length} of 10 entered).
                    </Text>
                </View>
            ) : null}
        </View>
    );
}

const styles = StyleSheet.create({
    input: {
        backgroundColor: "#fff",
        maxWidth: 250
    },
    chip: {
        marginTop: 8,
        paddingVertical: 8,
        paddingHorizontal: 12,
        borderRadius: 10,
        borderWidth: 1,
        borderColor: colors.error500,
        backgroundColor: colors.errBg,
        flexDirection: "row",
        alignItems: "center",
        gap: 7,
        alignSelf: "flex-start"
    },
    chipText: {
        fontFamily: fonts.semiBold,
        fontSize: 12,
        color: colors.ink,
        lineHeight: 17
    }
});
